$component-class: 'sn-comparison-type-2';

// Brand-specific highlight color for the promoted (SignNow) column.
// Semi-transparent tint — matches live signnow.com: rgba(206, 225, 255, 0.35).
// No exact design token exists for this specific blue tint; the closest is
// $color-brand-primary-lighter (#d1e6f6) but the value intentionally differs.
$highlighted-bg: rgb(206 225 255 / 35%);

// Fully opaque for the sticky header cell so scrolling rows don't bleed through.
$highlighted-header-bg: rgb(206 225 255);

// Stronger tint shown when hovering the highlighted column.
$highlighted-hover-bg: rgb(206 225 255 / 65%);

.#{$component-class} {
  margin: 0 auto;

  .me-section {
    max-width: 1280px;
  }

  &__intro {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-2xl);

    @include desktop {
      margin-bottom: var(--spacing-3xl);
    }

    .me-heading {
      margin-bottom: 0;
    }
  }

  // Sentinel div watched by IntersectionObserver: when it leaves the viewport
  // the sticky header drop-shadow class is toggled on the wrapper.
  &__sticky-sentinel {
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
    visibility: hidden;
  }

  &__table-wrapper {
    position: relative;
    width: 100%;
    margin-bottom: 52px;

    @include below-desktop {
      margin-bottom: 28px;
    }
  }

  &__table {
    border-spacing: 0;
    display: block;
    height: 100%;
    padding: 0;
    position: relative;
    width: 100%;
  }

  &__tbody {
    overflow-x: auto;
    scrollbar-width: none; /* Firefox */
    position: relative;
    display: block;

    &::-webkit-scrollbar {
      display: none; /* Chrome / Safari / Edge */
    }

    @include desktop {
      // Feature rows: reset column bg on hover (actual hover tint is applied by __row hover rules below)
      .sn-comparison-type-2__row:not(.sn-comparison-type-2__row--price):hover {
        .sn-comparison-type-2__column {
          background: inherit;
        }
      }

      // Price rows: no hover bg change at all — highlighted column keeps its static tint
      .sn-comparison-type-2__row--price:hover {
        .sn-comparison-type-2__column:not(.sn-comparison-type-2__column--highlighted) {
          background: transparent;
        }

        .sn-comparison-type-2__column--highlighted {
          background-color: $highlighted-bg;
        }
      }
    }

    .body-column {
      min-height: 56px;
      line-height: 20px;
      padding-top: 0;

      &--feature {
        font-size: map-get($font-sizes, md);
        line-height: 28px;
        padding-bottom: var(--spacing-sm);
        padding-left: 38px;
        padding-top: var(--spacing-sm);

        @include below-desktop {
          padding-left: 0;
          text-align: left;
        }
      }
    }
  }

  // ---- Header ----

  &__thead {
    display: block;
    background: var(--background-color-primary);
    min-height: 64px;
    position: sticky;
    top: 71px;
    width: 100%;
    z-index: 10;
    height: auto;

    @include desktop-md {
      top: 72px;
    }

    @include above($breakpoint-desktop-md) {
      top: 70px;
    }

    &-row {
      display: flex;
      align-items: flex-end;
      border-bottom: $border-size-md solid var(--color-neutral-neutral-lighter);
      position: relative;
      overflow-x: auto;
      scrollbar-width: none; /* Firefox */

      &::-webkit-scrollbar {
        display: none; /* Chrome / Safari / Edge */
      }
    }

    &-column {
      flex: 1 0 120px;
      font-size: map-get($font-sizes, sm);
      font-weight: map-get($font-weights, extra-bold);
      line-height: 24px;
      padding: 18px 1px 20px;
      width: 120px;

      @include desktop-md {
        font-size: map-get($font-sizes, md);
        padding-bottom: 30px;
        padding-top: 23px;
      }

      @include above($breakpoint-desktop-md) {
        font-size: map-get($font-sizes, xl);
        line-height: 28px;
        padding-bottom: 24px;
        padding-top: 26px;
      }

      &--dummu {
        font-family: "Open Sans", Arial, sans-serif;

        @include desktop-lg {
          padding-bottom: 14px;
        }
      }

      &--highlighted {
        border-top-left-radius: 12px;
        border-top-right-radius: 12px;
      }
    }

    &-column-logo {
      height: 30px;
      width: 88px;

      @include desktop-md {
        height: 36px;
        width: 106px;
      }

      @include above($breakpoint-desktop-md) {
        height: 42px;
        width: 124px;
      }
    }

    .me-text {
      display: none;

      @include desktop-lg {
        align-items: center;
        color: var(--text-color-tertiary);
        display: flex;
        max-width: 307px;
        min-height: 60px;
      }
    }
  }

  // Drop-shadow appears only when the header is actively sticky
  // (IntersectionObserver toggles --sticky-active on the wrapper).
  &__table-wrapper--sticky-active &__th {
    box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
  }

  &__col-label {
    display: block;

    // Matches live site: 24px / weight 800 for competitor brand names.
    font-size: map-get($font-sizes, 2xl);
    font-weight: map-get($font-weights, extra-bold);
    color: var(--text-color-primary);
    line-height: map-get($line-heights, sm);
  }

  &__col-logo {
    display: block;
    max-width: 120px;
    height: auto;
    margin: 0 auto;
  }

  // ---- Rows ----

  &__row {
    position: relative;
    display: flex;

    @include desktop-md {
      min-width: calc(100vw - 56px);
    }

    @include above($breakpoint-desktop-md) {
      min-width: 100%;
    }

    &::before {
      content: '';
      position: absolute;
      height: 100%;
      width: 100%;
      z-index: 1;
      pointer-events: none;
      transition: box-shadow 0.18s ease;

      @include below-desktop {
        display: none;
      }
    }

    &:hover::before {
      border-radius: var(--radius-size-md);
      box-shadow: 0 6px 20px rgb(0 0 0 / 8%);
    }

    &:hover .#{$component-class}__td {
      background-color: var(--background-color-secondary);
    }

    &:hover .#{$component-class}__td--highlighted {
      background-color: $highlighted-hover-bg;
    }

    &:last-child .#{$component-class}__td--highlighted {
      border-radius: 0 0 12px 12px;
    }

    &:last-child .#{$component-class}__td {
      border-bottom: none;
    }

    // Collapsed-state fade: bottom gradient overlay on the last visible feature row,
    // hinting that more rows are revealed by the "Show more" button.
    // Matches live signnow.com: 48px tall, transparent → page background, pinned to the row bottom.
    &--fade::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 48px;
      background: linear-gradient(to bottom, rgb(255 255 255 / 0%), var(--background-color-primary, #fff));
      pointer-events: none;
      z-index: 3;
    }
  }

  &__column {
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: center;
    min-width: 39%;
    text-align: center;

    @include desktop {
      padding-top: 28px;
      min-width: auto;
    }

    &--feature {
      font-weight: map-get($font-weights, semi-bold);
      line-height: 20px;
      padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) 0;
      z-index: 2;
      justify-content: flex-start;
      text-align: left;

      @include desktop {
        flex: 25%;
      }
    }

    &--dummu {
      flex: 25%;
      font-size: map-get($font-sizes, xs);
      font-weight: map-get($font-weights, normal);
      justify-content: flex-start;
      line-height: 16px;
      min-width: 33%;
      padding-left: 0;
      text-align: left;
      font-family: "Open Sans", Arial, sans-serif;

      @include above($breakpoint-desktop-md) {
        padding-left: 38px;
        padding-top: 0;
        flex: 25%;
      }

      @include below-desktop {
        min-width: 39%;
      }
    }

    &--highlighted {
      background-color: $highlighted-bg;

      &--last {
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;
      }
    }
  }

  // ---- Price rows ----

  &__row--price {
    // Disable all hover effects inherited from __row
    &::before {
      display: none;
    }

    // Plan name cell
    .body-column--feature {
      font-size: map-get($font-sizes, 2xl);
      font-weight: map-get($font-weights, semi-bold);
      line-height: 32px;
      padding: var(--spacing-xl) 0 var(--spacing-xl) 38px;
      color: var(--text-color-primary);

      @include below-desktop {
        padding-left: 0;
      }
    }

    // Value cells: stack price block vertically and center it
    td.body-column {
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      padding: 19px 1px 40px;
    }

    // Competitor (non-highlighted) column prices are muted
    td.body-column:not(.#{$component-class}__column--highlighted) {
      .#{$component-class}__price-currency,
      .#{$component-class}__price-amount,
      .#{$component-class}__price-period {
        color: var(--text-color-tertiary);
      }
    }
  }

  &__price-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__price {
    display: flex;
    align-items: flex-start;
    margin: 0;
    line-height: 1;
  }

  &__price-currency {
    font-size: map-get($font-sizes, md);
    font-weight: map-get($font-weights, semi-bold);
    line-height: 12px;
    color: var(--text-color-primary);
    padding-top: 4px;
  }

  &__price-amount {
    font-size: map-get($font-sizes, 4xl);
    font-weight: map-get($font-weights, bold);
    line-height: 24px;
    color: var(--text-color-primary);
  }

  &__price-period {
    font-size: map-get($font-sizes, xs);
    font-weight: map-get($font-weights, semi-bold);
    line-height: 18px;
    color: var(--text-color-primary);
    text-align: center;
    margin: 0;
  }

  &__price-link {
    display: block;
    margin-top: var(--spacing-xs);
    font-size: map-get($font-sizes, sm);
    font-weight: map-get($font-weights, semi-bold);
    color: var(--color-brand-primary, #0777cf);
    text-decoration: none;
    text-align: center;
    position: relative;

    @include below-desktop {
      &:hover {
        text-decoration: underline;
      }
    }

    @include desktop {
      &::before {
        background: var(--color-brand-primary, #0777cf);
        bottom: -2px;
        content: '';
        display: block;
        height: 2px;
        position: absolute;
        right: 0;
        transition: width 0.15s ease;
        width: 0;
        will-change: width;
      }

      &:hover::before {
        left: 0;
        width: 100%;
      }
    }

    &:focus-visible {
      outline: 2px solid var(--color-brand-primary, #0777cf);
      outline-offset: 2px;
      border-radius: var(--radius-size-sm);
    }
  }

  &__icon {
    display: inline-block;
    flex-shrink: 0;

    &--check,
    &--x {
      vertical-align: middle;
    }
  }

  &__cell-text {
    font-size: map-get($font-sizes, sm);
    color: var(--text-color-primary);
  }

  // ---- Tooltip cell ----
  // Matches live signnow.com: dark popover (#2F373E), white 12/16 text, 8x12 padding,
  // 4px radius, 240px wide, centered above the trigger. Hidden via visibility; revealed on
  // hover/focus or by toggling the `is-visible` class.
  &__tooltip {
    position: relative;
    display: inline-block;
    outline: none;
  }

  &__tooltip-description {
    // Inherit the cell-text sizing; the trigger inside is bolded below.
    display: inline;
  }

  &__tooltip-trigger {
    font-weight: map-get($font-weights, bold);
    color: var(--text-color-primary);
    cursor: help;
  }

  &__tooltip-text {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 8px;
    width: 240px;
    max-width: 240px;
    padding: 8px 12px;
    border-radius: 4px;
    background-color: #2f373e;
    color: var(--color-neutral-white, #fff);
    font-size: 12px;
    font-weight: map-get($font-weights, normal);
    line-height: 16px;
    text-align: center;
    white-space: normal;
    z-index: 20;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s ease, visibility 0.15s ease;
    pointer-events: none;
  }

  &__tooltip:hover &__tooltip-text,
  &__tooltip:focus-visible &__tooltip-text,
  &__tooltip-text.is-visible {
    visibility: visible;
    opacity: 1;
  }

  // ---- Show more toggle ----

  &__show-more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-sm);
  }

  &__show-more-teaser {
    font-size: map-get($font-sizes, md);
    color: var(--text-color-primary, #0777cf);
    font-weight: map-get($font-weights, bold);

    @include below-desktop {
      font-size: map-get($font-sizes, sm);
      line-height: 24px;
    }
  }

  &__toggle {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-2xs);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-size: map-get($font-sizes, md);
    font-weight: map-get($font-weights, bold);
    color: var(--color-brand-primary, #0777cf);
    text-decoration: none;

    @include below-desktop {
      font-size: map-get($font-sizes, sm);
      line-height: 24px;
      text-decoration: underline;
    }

    &::after {
      content: '';
      display: inline-block;
      width: 16px;
      height: 16px;
      background-image: url('https://marketing-static.signnow.com/386/images/global/link-arrow.svg');
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      transform: rotate(90deg); /* arrow points down (collapsed state) */
      right: -25px;
      position: absolute;
      top: 0;
      transition: transform 0.2s ease;
    }

    @include desktop {
      &::before {
        background: var(--color-brand-primary, #0777cf);
        bottom: -3px;
        content: "";
        display: block;
        height: 2px;
        position: absolute;
        right: 0;
        transition: width .15s ease;
        width: 0;
        will-change: width;
      }

      &:hover::before {
        left: 0;
        width: 100%;
      }
    }

    &[aria-expanded='true']::after {
      transform: rotate(-90deg); /* arrow points up (expanded state) */
    }
  }
}
